const dayMap = [
  'SUN',
  'MON',
  'TUE',
  'WED',
  'THU',
  'FRI',
  'SAT',
];

const hour24ToMinutes = (hour24) => {
  if (!hour24 || !hour24.includes(':')) return null; // Ensure the format is valid
  const [hour, minute] = hour24.split(':');
  const parsedHour = parseInt(hour, 10);
  const parsedMinute = parseInt(minute, 10);
  return !isNaN(parsedHour) && !isNaN(parsedMinute) ? parsedHour * 60 + parsedMinute : null;
};

/**
 * Find the closest time that the store will open after today
 * @param clientDay
 * @param operationalHours
 */
const generateNextOpenTime = ({ clientDay, operationalHours }) => {
  if (!operationalHours || !Array.isArray(operationalHours) || operationalHours.length === 0) {
    return {
      text: 'Closed',
      isOpen: false,
      untilDay: '',
      untilTime: '',
    };
  }

  const todayIdx = dayMap.indexOf(clientDay);
  const newArr = [...dayMap.slice(todayIdx + 1), ...dayMap.slice(0, todayIdx + 1)];

  for (const day of newArr) {
    const storeOperationalHours = operationalHours.find((operationalHour) => day === operationalHour.day);
    if (storeOperationalHours && storeOperationalHours.openTime) {
      return {
        text: 'Closed',
        isOpen: false,
        untilDay: storeOperationalHours.day,
        untilTime: storeOperationalHours.openTime,
      };
    }
  }

  return {
    text: 'Closed',
    isOpen: false,
    untilDay: '',
    untilTime: '',
  };
};

/**
 * Generates the open/close status of the store
 * @param operationalHours
 * @returns {{ isOpen: boolean, untilTime: string, text: string, untilDay: string }}
 */
export const generateTimeDescription = (operationalHours) => {
  if (!operationalHours || !Array.isArray(operationalHours) || operationalHours.length === 0) {
    return {
      text: 'Closed',
      isOpen: false,
      untilDay: '',
      untilTime: '',
    };
  }

  const clientDate = new Date();
  const clientDay = dayMap[clientDate.getDay()];
  const clientTime = clientDate.toLocaleTimeString('en-US', {
    hour12: false,
    hour: '2-digit',
    minute: '2-digit',
  });

  const todayOperationalHour = operationalHours.find((opHour) => opHour.day === clientDay);

  if (todayOperationalHour) {
    const currentTimeInMinutes = hour24ToMinutes(clientTime);
    const todayOpenTimeInMinutes = hour24ToMinutes(todayOperationalHour.openTime);
    const todayCloseTimeInMinutes = hour24ToMinutes(todayOperationalHour.closeTime);

    if (currentTimeInMinutes === null || todayOpenTimeInMinutes === null || todayCloseTimeInMinutes === null) {
      return {
        text: 'Closed',
        isOpen: false,
        untilDay: '',
        untilTime: '',
      };
    }

    const isStoreOpenNow =
      currentTimeInMinutes >= todayOpenTimeInMinutes && currentTimeInMinutes < todayCloseTimeInMinutes;

    if (isStoreOpenNow) {
      return {
        text: 'Open Now',
        isOpen: true,
        untilDay: todayOperationalHour.day,
        untilTime: todayOperationalHour.closeTime,
      };
    }

    const isBeforeStoreOpen = currentTimeInMinutes < todayOpenTimeInMinutes;
    if (isBeforeStoreOpen) {
      return {
        text: 'Closed',
        isOpen: false,
        untilDay: todayOperationalHour.day,
        untilTime: todayOperationalHour.openTime,
      };
    }
  }

  return generateNextOpenTime({ clientDay, operationalHours });
};

export const convertHour24ToHour12 = (hour24) => {
  if (!hour24 || !hour24.includes(':')) return null;
  const [hour, minute] = hour24.split(':');
  const parsedHour = parseInt(hour, 10);
  const period = parsedHour >= 12 ? 'PM' : 'AM';
  const hour12 = parsedHour % 12 || 12;
  return `${hour12}:${minute} ${period}`;
};

export const capitalizeFirstLetter = (str) => {
  return typeof str === 'string' && str.length > 0 ? str.charAt(0).toUpperCase() + str.slice(1).toLowerCase() : '';
};
